import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/design-system/layout/AppLayout";
import {
  ChevronDown,
  Save,
  Eye,
  Minimize2,
  Maximize2,
  Sparkles,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";

type Section = "basic" | "venue" | "registration" | "divisions" | "addons" | "coupons" | "forms" | "sponsors" | "waiver" | "communications" | "schedule" | "settings";

interface EventData {
  name: string;
  type: string;
  sport: string;
  gender: string;
  description: string;
  startDate: string;
  endDate: string;
}

const SECTIONS: Array<{
  id: Section;
  label: string;
  icon: string;
}> = [
  { id: "basic", label: "Basic Info", icon: "📋" },
  { id: "venue", label: "Venue", icon: "📍" },
  { id: "registration", label: "Registration", icon: "✓" },
  { id: "divisions", label: "Divisions & Age Groups", icon: "👥" },
  { id: "addons", label: "Add-ons & Extras", icon: "🎁" },
  { id: "coupons", label: "Coupons", icon: "🎟️" },
  { id: "forms", label: "Forms", icon: "📝" },
  { id: "sponsors", label: "Sponsors", icon: "🤝" },
  { id: "waiver", label: "Waiver", icon: "📄" },
  { id: "communications", label: "Communications", icon: "📧" },
  { id: "schedule", label: "Schedule Requests", icon: "📅" },
  { id: "settings", label: "Settings", icon: "⚙️" },
];

function BasicInfoSection() {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          Event Type
        </label>
        <select className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20">
          <option>Tournament</option>
          <option>Showcase</option>
          <option>Camp</option>
          <option>Clinic</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          Sport
        </label>
        <input
          type="text"
          placeholder="e.g., Basketball, Volleyball, Soccer"
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Start Date
          </label>
          <input
            type="date"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            End Date
          </label>
          <input
            type="date"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          Description
        </label>
        <textarea
          placeholder="Tell us about this event..."
          rows={4}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
        />
      </div>
    </div>
  );
}

function VenueSection() {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          Venue Name
        </label>
        <input
          type="text"
          placeholder="Select or create a venue"
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            City
          </label>
          <input
            type="text"
            placeholder="Kansas City"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            State
          </label>
          <input
            type="text"
            placeholder="MO"
            maxLength={2}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </div>

      <button className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium">
        <Plus className="w-4 h-4" />
        Add New Venue
      </button>
    </div>
  );
}

function RegistrationSection() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Registration Opens
          </label>
          <input type="date" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Registration Closes
          </label>
          <input type="date" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          Entry Fee
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
          <input
            type="number"
            placeholder="695.00"
            className="w-full rounded-lg border border-border bg-background pl-7 pr-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Registration Type
        </label>
        <div className="space-y-2">
          {["Team-based", "Individual", "Hybrid"].map((type) => (
            <label key={type} className="flex items-center gap-2">
              <input type="radio" name="regType" className="rounded" />
              <span className="text-sm text-foreground">{type}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function DivisionsSection() {
  return (
    <div className="space-y-4">
      <div className="space-y-3 mb-4">
        {["14U", "15U", "16U", "17U"].map((div) => (
          <div key={div} className="flex items-center justify-between p-3 border border-border rounded-lg">
            <span className="text-sm font-medium text-foreground">{div}</span>
            <button className="text-muted-foreground hover:text-foreground transition-colors">×</button>
          </div>
        ))}
      </div>
      <button className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium">
        <Plus className="w-4 h-4" />
        Add Division
      </button>
    </div>
  );
}

function CollapsibleSection({
  section,
  isOpen,
  onToggle,
}: {
  section: { id: Section; label: string; icon: string };
  isOpen: boolean;
  onToggle: () => void;
}) {
  const renderContent = () => {
    switch (section.id) {
      case "basic":
        return <BasicInfoSection />;
      case "venue":
        return <VenueSection />;
      case "registration":
        return <RegistrationSection />;
      case "divisions":
        return <DivisionsSection />;
      default:
        return (
          <div className="p-4 bg-secondary rounded-lg text-sm text-muted-foreground">
            {section.label} section coming soon...
          </div>
        );
    }
  };

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-secondary transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-lg">{section.icon}</span>
          <h3 className="font-semibold text-foreground text-left">{section.label}</h3>
        </div>
        <ChevronDown
          className={cn("w-5 h-5 text-muted-foreground transition-transform", {
            "rotate-180": isOpen,
          })}
        />
      </button>

      {isOpen && (
        <div className="border-t border-border p-4 bg-card">{renderContent()}</div>
      )}
    </div>
  );
}

export default function EventEditor() {
  const navigate = useNavigate();
  const [eventName, setEventName] = useState("Summer Basketball Classic");
  const [openSections, setOpenSections] = useState<Set<Section>>(new Set(["basic"]));
  const [previewOpen, setPreviewOpen] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  const toggleSection = (id: Section) => {
    const newOpen = new Set(openSections);
    if (newOpen.has(id)) {
      newOpen.delete(id);
    } else {
      newOpen.add(id);
    }
    setOpenSections(newOpen);
  };

  return (
    <AppLayout userName="Sarah Johnson" userRole="Tournament Operator">
      <div className="min-h-screen bg-background flex flex-col lg:flex-row">
        {/* Top Preview Pane */}
        <div
          className={cn(
            "border-b lg:border-b-0 lg:border-r border-border bg-white overflow-hidden transition-all duration-300",
            previewOpen ? "lg:w-1/3 h-96 lg:h-full" : "lg:w-0 h-0"
          )}
        >
          {previewOpen && (
            <div className="p-6 space-y-4 h-full overflow-y-auto">
              <div className="sticky top-0 bg-white pb-4 border-b border-border">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-foreground">Live Preview</h3>
                  <select className="text-xs bg-secondary rounded px-2 py-1 border-0 outline-none">
                    <option>Public Page</option>
                    <option>Coach Portal</option>
                    <option>Mobile</option>
                    <option>Print</option>
                  </select>
                </div>
                <div className="inline-block bg-info-light text-info text-xs font-semibold px-2.5 py-1 rounded-full">
                  Status: DRAFT
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">EVENT NAME</p>
                  <p className="text-lg font-bold text-foreground">{eventName}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">ENTRY FEE</p>
                  <p className="text-2xl font-bold text-primary">$695</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">DIVISIONS</p>
                  <div className="flex flex-wrap gap-2">
                    {["14U", "15U", "16U", "17U"].map((div) => (
                      <span key={div} className="text-xs bg-secondary text-foreground px-2 py-1 rounded">
                        {div}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Click elements below to edit directly
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Main Content Pane */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Sticky Header */}
          <div className="border-b border-border bg-white p-4 flex-shrink-0">
            <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
              <div className="flex-1">
                {isEditing ? (
                  <input
                    autoFocus
                    value={eventName}
                    onChange={(e) => setEventName(e.target.value)}
                    onBlur={() => setIsEditing(false)}
                    className="w-full text-2xl font-bold text-foreground bg-transparent border-b-2 border-primary outline-none"
                  />
                ) : (
                  <h1
                    onClick={() => setIsEditing(true)}
                    className="text-2xl font-bold text-foreground cursor-pointer hover:text-primary transition-colors"
                  >
                    {eventName}
                  </h1>
                )}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => navigate("/events/new/flow")}
                  className="hidden lg:flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary text-foreground hover:bg-muted transition-colors text-sm font-medium"
                >
                  <Sparkles className="w-4 h-4" />
                  Registration Flow
                </button>
                <button className="hidden lg:flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary text-foreground hover:bg-muted transition-colors text-sm font-medium">
                  <Eye className="w-4 h-4" />
                  {previewOpen ? "Hide" : "Show"} Preview
                </button>
                <button className="hidden lg:flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity text-sm font-medium">
                  <Save className="w-4 h-4" />
                  Save Draft
                </button>
              </div>
            </div>
          </div>

          {/* Section Navigation + Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-4xl mx-auto p-6 space-y-4">
              {/* Mobile Preview Toggle */}
              <div className="lg:hidden mb-4">
                <button
                  onClick={() => setPreviewOpen(!previewOpen)}
                  className="flex items-center gap-2 w-full px-4 py-2 rounded-lg bg-secondary text-foreground hover:bg-muted transition-colors text-sm font-medium"
                >
                  {previewOpen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  {previewOpen ? "Hide" : "Show"} Preview
                </button>
              </div>

              {/* Section Navigation Bar */}
              <div className="flex gap-2 overflow-x-auto pb-4 -mx-6 px-6">
                {SECTIONS.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => toggleSection(section.id)}
                    className={cn(
                      "flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap",
                      openSections.has(section.id)
                        ? "bg-primary text-white"
                        : "bg-secondary text-foreground hover:bg-muted"
                    )}
                  >
                    {section.icon} {section.label}
                  </button>
                ))}
              </div>

              {/* AI Suggestion Banner */}
              <div className="flex items-start gap-3 p-4 rounded-lg border-l-4 border-primary bg-primary/5 mb-4">
                <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-foreground font-medium mb-2">
                    ✨ You mentioned "16U division" but the form allows up to 17U. Want to add it?
                  </p>
                  <div className="flex gap-2">
                    <button className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary text-white hover:opacity-90">
                      Yes, add 17U
                    </button>
                    <button className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-secondary text-foreground hover:bg-muted">
                      No, just 16U
                    </button>
                  </div>
                </div>
              </div>

              {/* Collapsible Sections */}
              <div className="space-y-3">
                {SECTIONS.map((section) => (
                  <CollapsibleSection
                    key={section.id}
                    section={section}
                    isOpen={openSections.has(section.id)}
                    onToggle={() => toggleSection(section.id)}
                  />
                ))}
              </div>

              {/* Footer Actions */}
              <div className="flex gap-3 mt-8 mb-4 sticky bottom-0 py-4 bg-gradient-to-t from-background to-transparent">
                <button
                  onClick={() => navigate("/events")}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border text-foreground hover:bg-secondary transition-colors font-medium"
                >
                  Cancel
                </button>
                <button className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-secondary text-foreground hover:bg-muted transition-colors font-medium ml-auto">
                  <Save className="w-4 h-4" />
                  Save Draft
                </button>
                <button className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity font-medium">
                  <Save className="w-4 h-4" />
                  Publish Event
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
